import { useState, useEffect, useRef } from 'react'
import { useEditMode } from '@/contexts/EditModeContext'
import { useAuth } from '@/contexts/AuthContext'
import { saveField, type EditableTable } from '@/lib/editApi'
import { Loader2, Check, X } from 'lucide-react'

interface EditableTextProps {
  // Modo "default": editar un campo escalar de una tabla.
  table?: EditableTable
  id?: string
  field?: string
  value: string | null
  // Modo "custom": el caller maneja el guardado (ej. updates a opciones JSON).
  // Si se pasa, reemplaza al saveField default. Recibe el valor nuevo y devuelve { error }.
  customSave?: (newValue: string) => Promise<{ error: Error | null }>
  multiline?: boolean
  placeholder?: string
  className?: string
  as?: 'span' | 'div' | 'p' | 'h1' | 'h2' | 'h3' | 'h4'
  onSaved?: (newValue: string) => void
}

export default function EditableText({
  table,
  id,
  field,
  value,
  customSave,
  multiline = false,
  placeholder = '(vacío)',
  className = '',
  as: Tag = 'span',
  onSaved
}: EditableTextProps) {
  const { isEditMode } = useEditMode()
  const { profile } = useAuth()
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState(value || '')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [localValue, setLocalValue] = useState(value || '')
  const inputRef = useRef<HTMLInputElement | HTMLTextAreaElement>(null)

  useEffect(() => {
    setLocalValue(value || '')
    setDraft(value || '')
  }, [value])

  useEffect(() => {
    if (editing && inputRef.current) {
      inputRef.current.focus()
      inputRef.current.select()
    }
  }, [editing])

  const startEdit = (e?: React.MouseEvent) => {
    if (!isEditMode || saving) return
    // No propagar el click al wrapper (ej. <Link>) para que no navegue.
    e?.stopPropagation()
    e?.preventDefault()
    setDraft(localValue)
    setEditing(true)
    setError(null)
  }

  const cancel = () => {
    setEditing(false)
    setDraft(localValue)
    setError(null)
  }

  const save = async () => {
    if (!profile?.id) {
      setError('No autenticado')
      return
    }
    if (draft === localValue) {
      setEditing(false)
      return
    }
    setSaving(true)
    setError(null)
    let err: Error | null = null
    if (customSave) {
      const res = await customSave(draft)
      err = res.error
    } else {
      if (!table || !id || !field) {
        setSaving(false)
        setError('Faltan parámetros (table/id/field) o customSave')
        return
      }
      const res = await saveField({
        table,
        id,
        field,
        oldValue: localValue,
        newValue: draft,
        editedBy: profile.id
      })
      err = res.error
    }
    setSaving(false)
    if (err) {
      setError(err.message)
      return
    }
    setLocalValue(draft)
    setEditing(false)
    onSaved?.(draft)
  }

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      e.preventDefault()
      cancel()
      return
    }
    if (e.key === 'Enter' && !multiline) {
      e.preventDefault()
      save()
      return
    }
    if (e.key === 'Enter' && multiline && (e.ctrlKey || e.metaKey)) {
      e.preventDefault()
      save()
    }
  }

  if (editing) {
    const InputTag = multiline ? 'textarea' : 'input'
    return (
      <span className="inline-flex items-start gap-1 align-middle w-full">
        <InputTag
          ref={inputRef as any}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={onKeyDown}
          disabled={saving}
          rows={multiline ? Math.max(2, draft.split('\n').length) : undefined}
          className={`flex-1 min-w-0 px-2 py-1 border border-amber-400 rounded bg-white text-gray-900 outline-none focus:ring-2 focus:ring-amber-300 ${multiline ? 'resize-y' : ''}`}
        />
        <span className="inline-flex items-center gap-0.5 shrink-0 mt-0.5">
          {saving ? (
            <Loader2 className="w-4 h-4 animate-spin text-amber-500" />
          ) : (
            <>
              <button
                type="button"
                onClick={save}
                title="Guardar (Enter)"
                className="p-1 rounded hover:bg-green-100 text-green-600"
              >
                <Check className="w-4 h-4" />
              </button>
              <button
                type="button"
                onClick={cancel}
                title="Cancelar (Esc)"
                className="p-1 rounded hover:bg-red-100 text-red-500"
              >
                <X className="w-4 h-4" />
              </button>
            </>
          )}
        </span>
        {error && (
          <span className="block w-full text-xs text-red-600 mt-1">{error}</span>
        )}
      </span>
    )
  }

  const showPlaceholder = !localValue
  const display = showPlaceholder ? placeholder : localValue
  // Solo mostramos el indicador en HOVER, no permanente. Así no
  // satura la pantalla cuando el modo edición está activo.
  const editableClass = isEditMode
    ? 'cursor-text rounded outline outline-1.5 outline-dashed outline-transparent outline-offset-2 hover:outline-amber-400 hover:bg-amber-50/40 transition-colors'
    : ''

  return (
    <Tag
      className={`${className} ${editableClass} ${showPlaceholder && isEditMode ? 'text-gray-400 italic' : ''}`}
      onClick={startEdit}
      title={isEditMode ? 'Click para editar' : undefined}
    >
      {display}
    </Tag>
  )
}
